import { useEffect } from "react";
import JonesboroHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in Jonesboro?", a: "We offer same-day processing options that can deliver your passport in as little as 24 hours. For Jonesboro and Northeast Arkansas residents, our standard expedited service takes 7–10 business days, while our emergency service delivers in 24–72 hours." },
  { q: "Where is the nearest passport acceptance facility near Jonesboro?", a: "The closest acceptance facilities are at the Jonesboro Post Office (downtown) and the ASU Student Center. We also serve residents in Paragould, Brookland, and Bono with convenient drop-off options." },
  { q: "Do I need an appointment for a new passport in Jonesboro?", a: "Yes, most acceptance facilities require an appointment for new passport applications. However, our expedited service handles all the logistics for you, including appointment scheduling and document preparation." },
  { q: "Can I renew my passport by mail from Jonesboro?", a: "If your passport was issued within the last 15 years and you were 16 or older at the time, you can renew by mail. Our expedited renewal service handles everything from Jonesboro to the State Department." },
  { q: "What documents do I need for a child passport in Jonesboro?", a: "For children under 16, you need proof of citizenship, parental identification, and both parents must appear (or provide consent). We guide you through every step to avoid delays." },
  { q: "How much does expedited passport service cost for Jonesboro residents?", a: "Our rates are typically 30–100% lower than FedEx and Staples. Government fees are $130 for a passport book (adult), plus our expedited service fee. We offer a best price guarantee." },
  { q: "What if my passport is lost or stolen in Jonesboro?", a: "You need to file a DS-64 form and apply in person with a DS-11 form. Our emergency service can help you get a replacement passport within 24–72 hours." },
  { q: "Can I get a passport photo taken in Jonesboro?", a: "Yes, many pharmacies and shipping centers in Jonesboro offer passport photo services. We also provide photo guidelines to ensure your photo meets State Department requirements." },
];

export default function JonesboroPage() {
  useEffect(() => {
    document.title = "Emergency Passport Jonesboro, AR — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/jonesboro/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Jonesboro, AR — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Northeast Arkansas ASU students, medical professionals, and families.");
    setMeta("geo.placename", "Jonesboro, Arkansas");
    setMeta("geo.region", "US-AR");
    setMeta("geo.position", "35.8423;-90.7043");
    setMeta("ICBM", "35.8423, -90.7043");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Arkansas", item: "https://fastpassportcenter.com/arkansas/" },
          { "@type": "ListItem", position: 3, name: "Jonesboro Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Jonesboro — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Jonesboro and Northeast Arkansas travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Jonesboro",
          containedInPlace: { "@type": "State", name: "Arkansas" },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: { "@type": "LocalBusiness", name: "Fast Passport Center" },
        areaServed: { "@type": "City", name: "Jonesboro" },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <JonesboroHeroSection />

      {/* StickyNav */}
      <nav className="sticky top-[70px] z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 overflow-x-auto py-3 no-scrollbar">
          {["Services", "Processing", "Local Area", "Timing", "How It Works", "FAQ"].map((label) => (
            <a key={label} href={`#${label.toLowerCase().replace(/ /g, "-")}`} className="text-sm font-medium text-[#1A365D] hover:text-[#F6C344] whitespace-nowrap px-3 py-1 rounded-md hover:bg-[#EEF2F8] transition-all">
              {label}
            </a>
          ))}
          <a href="https://fastpassportcenter.com/checkout/" className="ml-auto text-sm font-bold bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] px-4 py-1.5 rounded-md whitespace-nowrap transition-all">
            Get Started
          </a>
        </div>
      </nav>

      {/* Processing Time */}
      <section id="processing" className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How Fast Can You Get a Passport in Jonesboro?</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Choose the speed that matches your travel timeline. From same-day emergency processing to standard expedited service.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { speed: "Same-Day Emergency", time: "24–72 Hours", price: "From $599", best: true, desc: "For travelers with immediate departure needs. Our fastest option with dedicated courier service." },
              { speed: "Expedited Rush", time: "3–5 Business Days", price: "From $349", best: false, desc: "Ideal for urgent travel within 2 weeks. Full tracking and document review included." },
              { speed: "Standard Expedited", time: "7–10 Business Days", price: "From $199", best: false, desc: "Great for planned travel in 3–6 weeks. Best value with reliable processing." },
            ].map((opt) => (
              <div key={opt.speed} className={`relative rounded-lg border p-8 flex flex-col ${opt.best ? "border-[#F6C344] bg-[#FEFDF8]" : "border-gray-200 bg-white"}`}>
                {opt.best && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F6C344] text-[#1A365D] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">Most Popular</span>}
                <h3 className="font-bold text-[#1A365D] text-xl mb-2">{opt.speed}</h3>
                <p className="text-[#F6C344] font-bold text-2xl mb-1">{opt.time}</p>
                <p className="text-gray-500 text-sm mb-4">{opt.desc}</p>
                <div className="mt-auto">
                  <p className="text-[#1A365D] font-bold text-lg mb-4">{opt.price}</p>
                  <a href="https://fastpassportcenter.com/checkout/" className="block text-center bg-[#1A365D] hover:bg-[#0F2440] text-white font-semibold text-sm py-3 rounded-md transition-all whitespace-nowrap">
                    Select This Speed
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">What We Offer</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services for Jonesboro &amp; Northeast Arkansas</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: "ri-flashlight-line", title: "Emergency Passport", desc: "24–72 hour processing for urgent departures from Jonesboro Regional Airport." },
              { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for expired passports. Mail-in service available for eligible renewals." },
              { icon: "ri-passport-line", title: "New Passport", desc: "First-time application guidance with acceptance facility appointment scheduling." },
              { icon: "ri-alert-line", title: "Lost/Stolen Passport", desc: "DS-64 filing and replacement service. Get a new passport within 24–72 hours." },
              { icon: "ri-parent-line", title: "Child Passport", desc: "Under-16 passport services with parental consent and document preparation." },
              { icon: "ri-edit-line", title: "Name Change", desc: "Marriage, divorce, or legal name change updates with court document guidance." },
              { icon: "ri-stack-line", title: "Second Passport", desc: "For frequent travelers needing two valid passports simultaneously." },
              { icon: "ri-tools-line", title: "Damaged Passport", desc: "Replacement for water-damaged, torn, or otherwise compromised passports." },
              { icon: "ri-file-list-3-line", title: "Visa Document Prep", desc: "Document preparation and guidance for popular visa destinations." },
            ].map((s) => (
              <div key={s.title} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                  <i className={`${s.icon} text-[#1A365D] text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-lg mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Area */}
      <section id="local-area" className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Area Coverage</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services Across the Jonesboro Area</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div className="space-y-6 text-gray-600 leading-relaxed">
              <p>
                <strong className="text-[#1A365D]">Jonesboro is the heart of Northeast Arkansas and home to Arkansas State University (ASU),</strong> one of the largest universities in the state with over 14,000 students. Every year, hundreds of ASU students and faculty need passports for study abroad programs, international research, and academic conferences. We specialize in expedited passport processing for the academic community, including group processing for study abroad cohorts and faculty-led travel programs.
              </p>
              <p>
                <strong className="text-[#1A365D]">Northeast Arkansas Medical Center (NEA Baptist)</strong> and St. Bernards Medical Center employ physicians, nurses, and specialists who frequently travel to international medical conferences and continuing education programs. Our emergency passport service helps medical professionals get their travel documents fast, even with last-minute conference acceptances and research presentation deadlines.
              </p>
              <p>
                We also serve the broader Northeast Arkansas community, including <strong className="text-[#1A365D]">Paragould, Brookland, Bono, Bay, Lake City, and Monette</strong>. Whether you are a family in Greene County planning your first Caribbean cruise, a business owner in Craighead County with a trade mission to Mexico, or a retiree in the surrounding area planning a European tour, we provide the same fast, reliable service across the entire region.
              </p>
              <p>
                The Jonesboro area is served by <strong className="text-[#1A365D]">Jonesboro Regional Airport (JBR)</strong> for regional connections, but most international travelers connect through Memphis International Airport (MEM), about 75 miles east. Our expedited processing ensures you have your passport in hand well before your departure, even if you are flying out of Memphis, Little Rock, or Nashville.
              </p>
            </div>
            <div className="space-y-4">
              <div className="bg-[#F7F8FA] rounded-lg p-6">
                <h4 className="font-bold text-[#1A365D] mb-4">Areas We Serve</h4>
                <div className="grid grid-cols-2 gap-3">
                  {["Jonesboro", "Paragould", "Brookland", "Bono", "Bay", "Lake City", "Monette", "Trumann", "Walnut Ridge", "Hoxie", "Rector", "Piggott"].map((area) => (
                    <div key={area} className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-map-pin-line text-[#F6C344]"></i>{area}
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#F7F8FA] rounded-lg p-6">
                <h4 className="font-bold text-[#1A365D] mb-4">Nearby Airports</h4>
                <div className="space-y-2">
                  {[
                    { name: "Memphis International (MEM)", dist: "75 miles east" },
                    { name: "Little Rock National (LIT)", dist: "130 miles west" },
                    { name: "Jonesboro Regional (JBR)", dist: "Local regional" },
                  ].map((a) => (
                    <div key={a.name} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{a.name}</span>
                      <span className="text-gray-400">{a.dist}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Travel Timing */}
      <section id="timing" className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Travel Timing</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Best Times to Apply from Jonesboro</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { season: "Spring (Mar–May)", tag: "Peak Season", desc: "Spring break and early summer trip planning. Apply 8–10 weeks before travel. Many ASU students travel for Maymester programs." },
              { season: "Summer (Jun–Aug)", tag: "High Demand", desc: "Peak processing season. Apply early if possible. Emergency services remain fully available for last-minute summer trips." },
              { season: "Fall/Winter (Sep–Feb)", tag: "Best Value", desc: "Fastest processing times of the year. Ideal for holiday cruises and winter escapes. Standard expedited often delivers faster than quoted." },
            ].map((t) => (
              <div key={t.season} className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-[#1A365D] text-lg">{t.season}</h3>
                  <span className="text-xs font-semibold bg-[#EEF2F8] text-[#1A365D] px-2 py-1 rounded-full whitespace-nowrap">{t.tag}</span>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Destinations */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Popular Destinations</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Jonesboro Travelers Are Going</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { dest: "Cancun & Riviera Maya", icon: "ri-sun-line", desc: "Spring break and family beach vacations. Most popular from March through June." },
              { dest: "European River Cruises", icon: "ri-ship-line", desc: "Retirees and couples exploring the Rhine and Danube. Peak season: April–October." },
              { dest: "Italy & Spain", icon: "ri-building-2-line", desc: "ASU study abroad programs in Rome, Florence, and Barcelona. Maymester and fall semester." },
              { dest: "Caribbean Cruises", icon: "ri-anchor-line", desc: "Family cruises from New Orleans and Galveston. Year-round demand with summer peaks." },
            ].map((d) => (
              <div key={d.dest} className="bg-[#F7F8FA] rounded-lg p-6 text-center hover:bg-[#EEF2F8] transition-all">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white mx-auto mb-4">
                  <i className={`${d.icon} text-[#1A365D] text-2xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] mb-2">{d.dest}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-[#1A365D] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Traveler Reviews</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">What Jonesboro Residents Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "Dr. Sarah M.", location: "Northeast Arkansas Medical Center", text: "I had a conference in Berlin in 10 days and my passport had expired. Fast Passport Center had my new passport in my hands in 48 hours. Incredible service for busy medical professionals." },
              { name: "Jake T.", location: "ASU Student", text: "My study abroad program to Spain was confirmed late and I needed a passport in under two weeks. They made it happen with time to spare. Every ASU student should know about this service." },
              { name: "Linda & Bob K.", location: "Paragould, AR", text: "We booked a Caribbean cruise out of New Orleans and realized our passports had expired. They walked us through the renewal process and we got them back in 5 days. Amazing!" },
            ].map((t, i) => (
              <div key={i} className="bg-white/10 backdrop-blur rounded-lg p-6 border border-white/10">
                <div className="flex items-center gap-1 mb-4">
                  {[1,2,3,4,5].map((s) => <i key={s} className="ri-star-fill text-[#F6C344] text-sm"></i>)}
                </div>
                <p className="text-white/90 text-sm leading-relaxed mb-4">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#F6C344] flex items-center justify-center text-[#1A365D] font-bold text-sm">{t.name[0]}</div>
                  <div>
                    <p className="text-white font-semibold text-sm">{t.name}</p>
                    <p className="text-white/60 text-xs">{t.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Acceptance Facilities */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Acceptance Facilities Near Jonesboro</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Visit these locations for in-person application submission. We handle the rest of the expedited process.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { name: "Jonesboro Post Office", address: "2400 S Caraway Rd, Jonesboro, AR 72401", phone: "(870) 972-4700", type: "Post Office", note: "Photo services available. Appointment recommended." },
              { name: "Craighead County Clerk", address: "511 S Main St, Jonesboro, AR 72401", phone: "(870) 933-4530", type: "County Clerk", note: "Accepts walk-ins. Limited hours on Fridays." },
            ].map((f) => (
              <div key={f.name} className="bg-[#F7F8FA] rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8]">
                    <i className="ri-government-line text-[#1A365D]"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1A365D] text-base">{f.name}</h3>
                    <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full border border-gray-200">{f.type}</span>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600"><i className="ri-map-pin-line text-[#F6C344]"></i>{f.address}</div>
                  <div className="flex items-center gap-2 text-gray-600"><i className="ri-phone-line text-[#F6C344]"></i>{f.phone}</div>
                  <div className="flex items-center gap-2 text-gray-500"><i className="ri-information-line text-[#F6C344]"></i>{f.note}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Why Choose Us</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Fast Passport Center vs. Other Options</h2>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-4 bg-[#1A365D] text-white text-sm font-semibold py-4 px-6">
              <div>Feature</div>
              <div className="text-center">Fast Passport Center</div>
              <div className="text-center">FedEx / Staples</div>
              <div className="text-center">DIY at Post Office</div>
            </div>
            {[
              ["Speed", "24 hrs – 10 days", "2–6 weeks", "8–11 weeks"],
              ["Price", "30–100% lower", "Expensive markup", "Lowest base cost"],
              ["Personal Support", "Dedicated agent", "Limited / None", "None"],
              ["Document Review", "Expert review included", "Extra fee", "Self-check"],
              ["Real-Time Tracking", "Yes", "Basic", "No"],
              ["State Dept. Courier", "Registered", "Reseller", "N/A"],
              ["Best Price Guarantee", "Yes", "No", "N/A"],
              ["A+ BBB Rating", "Yes", "Varies", "N/A"],
              ["Money-Back Guarantee", "Yes", "Varies", "No"],
            ].map((row, i) => (
              <div key={i} className={`grid grid-cols-4 py-4 px-6 text-sm ${i % 2 === 0 ? "bg-[#F7F8FA]" : "bg-white"}`}>
                <div className="text-[#1A365D] font-semibold">{row[0]}</div>
                <div className="text-center text-green-700 font-medium">{row[1]}</div>
                <div className="text-center text-gray-500">{row[2]}</div>
                <div className="text-center text-gray-500">{row[3]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mistakes */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Common Mistakes</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Don't Let These Errors Delay Your Trip</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Wrong Photo Size", desc: "Department of State requires 2x2 inches with a white background. Drugstore photos often fail. We provide exact photo specifications." },
              { title: "Expired Name Documents", desc: "If your name changed, bring a certified marriage certificate or court order. Photocopies are not accepted at acceptance facilities." },
              { title: "Incomplete DS-11 Forms", desc: "Missing signatures or wrong dates are the #1 reason for delays. We review every form before submission to catch errors early." },
              { title: "Wrong Payment Amount", desc: "Government fees change. We confirm current fees so your check or money order is accurate." },
              { title: "Missing Parental Consent", desc: "Children under 16 need both parents present. If one cannot attend, a notarized DS-3053 is required. We help with this." },
              { title: "Waiting Too Long", desc: "Standard processing takes 8–11 weeks. Many travelers wait until 2 weeks before their trip. Start your application as soon as you book." },
            ].map((m) => (
              <div key={m.title} className="bg-[#F7F8FA] rounded-lg p-6 border-l-4 border-[#F6C344]">
                <h3 className="font-bold text-[#1A365D] mb-2">{m.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Simple Process</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works in 4 Steps</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Start Online", desc: "Choose your processing speed and complete our secure online application. Takes 5 minutes." },
              { step: "02", title: "Document Prep", desc: "We review your documents and schedule your acceptance facility visit in Jonesboro or nearby." },
              { step: "03", title: "Courier Handoff", desc: "Our registered State Department courier picks up your sealed envelope and delivers it directly." },
              { step: "04", title: "Passport Delivered", desc: "Your passport arrives at your Jonesboro address via secure overnight shipping." },
            ].map((s) => (
              <div key={s.step} className="bg-white rounded-lg p-6 text-center border border-gray-200">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#EEF2F8] mx-auto mb-4">
                  <span className="text-[#1A365D] font-bold text-xl">{s.step}</span>
                </div>
                <h3 className="font-bold text-[#1A365D] mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <details key={i} className="bg-[#F7F8FA] rounded-lg border border-gray-200 group">
                <summary className="flex items-center justify-between p-5 cursor-pointer text-[#1A365D] font-semibold text-base hover:text-[#F6C344] transition-colors">
                  {faq.q}
                  <i className="ri-add-line text-[#F6C344] group-open:rotate-45 transition-transform"></i>
                </summary>
                <p className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Helpful Resources */}
      <section className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Links for Jonesboro Travelers</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "U.S. State Department", desc: "Official passport requirements and forms.", link: "https://travel.state.gov/content/travel/en/passports.html" },
              { title: "TSA PreCheck", desc: "Speed through security at Memphis and Little Rock airports.", link: "https://www.tsa.gov/precheck" },
              { title: "CDC Travel Health", desc: "Vaccination and health notices for your destination.", link: "https://wwwnc.cdc.gov/travel" },
              { title: "Smart Traveler Enrollment", desc: "Register your trip with the U.S. Embassy.", link: "https://step.state.gov/" },
              { title: "Global Entry", desc: "Expedited customs clearance for frequent international travelers.", link: "https://www.cbp.gov/travel/trusted-traveler-programs/global-entry" },
              { title: "ASU Study Abroad", desc: "Information for Arkansas State University students traveling abroad.", link: "https://www.astate.edu/international/study-abroad/" },
            ].map((r) => (
              <a key={r.title} href={r.link} target="_blank" rel="noopener noreferrer nofollow" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
                <h3 className="font-bold text-[#1A365D] mb-2 group-hover:text-[#F6C344] transition-colors">{r.title}</h3>
                <p className="text-gray-500 text-sm mb-3">{r.desc}</p>
                <span className="inline-flex items-center gap-1 text-[#1A365D] text-sm font-medium group-hover:text-[#F6C344] transition-colors">Visit <i className="ri-arrow-right-line"></i></span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <SEOCrossLinks currentRoute="/cities/jonesboro" state="AR" />

      {/* Final CTA */}
      <section className="bg-[#1A365D] py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Get Your Passport in Jonesboro?
          </h2>
          <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Don't let paperwork delays ruin your travel plans. Whether you are an ASU student, a medical professional, or a family planning a vacation, we are here to help you get your passport fast.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
              Start My Passport <i className="ri-arrow-right-line"></i>
            </a>
            <a href="tel:8772530084" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold text-base px-8 py-4 rounded-md transition-all whitespace-nowrap">
              <i className="ri-phone-line"></i>877-253-0084
            </a>
          </div>
          <p className="text-white/50 text-sm mt-6">Free consultation. No obligation. A+ BBB rated.</p>
        </div>
      </section>
    </div>
  );
}